import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { environment } from 'src/environments/environment';
import { SHELL_APP_CONFIG } from '../_interface/shellAppConfig';
import { getBrowserInfo } from '../_helpers/browser-info';
import { isNativeShell } from '../_helpers/native-shell';
import { WSService } from './ws.service';
import { ThemeService } from './theme.service';
import { LanguageService } from './language.service';
import { AuthorizationService } from './authorization.service';

/** Odluka koju server šalje uz odgovor na `version` frame. */
export interface ClarityPolicy {
  enabled: boolean;
  projectID?: string;
  /** Samo tagovi koje klijent ne može sam da zna (sesija, identitet uređaja). */
  tags?: { [key: string]: string };
}

const CLARITY_SRC = 'https://www.clarity.ms/tag/';

/**
 * Kod aplikacije iz verzije (`1.1.43-k-dev` → `k`) u čitljiv naziv za Clarity filter — iz `k`
 * se ne vidi šta je.
 *
 * Namerno NIJE kroz `translate`: tag postavlja klijent na svom jeziku, pa bi ista aplikacija
 * stizala kao "Kiosk", "Chiosco", "Kiosk"… i vrednosti filtera bi se rasule na tri jezika.
 * Ovo je vrednost za mašinu, ne tekst za korisnika, pa mora da bude stabilna.
 *
 * Kodovi su isti kao u izveštaju o browserima (`adminDeviceBrowsers.appNames`). Nepoznat kod
 * ostaje kakav je stigao, da nova aplikacija ne bi tiho nestala iz filtera.
 */
const APP_NAMES: { [code: string]: string } = {
  k: 'kiosk',
  w: 'waiter',
  kd: 'kitchen display',
  cd: 'orders display',
  a: 'admin',
  ds: 'digital signage',
  d: 'delivery',
  app: 'user app',
  pos: 'pos'
};

/**
 * Microsoft Clarity (snimanje sesija + heatmape).
 *
 * Ova klasa NE odlučuje da li se snima — odluku donosi server i šalje je kao odgovor na
 * `version` WS frame (`ClarityPolicyResolver` na backendu). Razlog: globalni prekidač mora
 * da radi trenutno i nad celom flotom, uključujući uređaje koji vrte stariji build; da je
 * uslov ovde, "ugasi svima" bi zavisilo od toga ko je već povukao novu verziju.
 *
 * Ali TAGOVE sastavlja klijent, jer klijent i jeste izvor skoro svih (verzija, browser, shell,
 * štampač, način plaćanja — sve to server zna samo zato što mu ih je klijent poslao). Slanje
 * natrag bi bilo obilaženje, i baš je tako nastala greška sa `companyID`: server ga do
 * `setCompany` frame-a nije ni znao, pa je uz snimak išla privremena firma. Sa servera stiže
 * samo ono što klijent stvarno ne zna — ID sesije i identitet uređaja.
 *
 * Posledice koje su namerne:
 *  - iza LocalServer-a se nikad ne snima, jer LocalServer taj frame ne implementira
 *    (offline-first — bez poziva ka internetu),
 *  - POS klijenti se ne snimaju (server ih preskače),
 *  - stari uređaji ispadaju sami preko praga Chromium baze u podešavanjima.
 *
 * Skripta se učitava tek kad stigne odgovor na version frame, dakle posle boot-a — start
 * aplikacije se ne usporava. Učitavanje je best-effort: greška se loguje i ignoriše, nikad
 * ne ruši aplikaciju.
 *
 * Ograničenje: Clarity nema podržano gašenje u toku sesije. Kad polisa pređe u isključeno,
 * uređaj koji već snima prestaje tek pri sledećem učitavanju stranice (WS komanda `reload`
 * to ubrzava ako je potrebno odmah).
 */
@Injectable({
  providedIn: 'root'
})
export class ClarityService {

  private loadedProjectID: string | null = null;

  /** Poslednji tagovi sa servera — čuvaju se da bi se pri osvežavanju slali zajedno sa klijentskim. */
  private serverTags: { [key: string]: string } = {};

  /** Poslednji prijavljeni korisnik — po promeni počinje nova Clarity sesija. */
  private lastUserID = 0;

  constructor(
    private ws: WSService,
    private logger: NGXLogger,
    private theme: ThemeService,
    private language: LanguageService,
    private auth: AuthorizationService
  ) {
    this.ws.observe('version').subscribe({
      next: msg => this.apply(msg?.data?.clarity),
      error: () => { }
    });

    // Sve ovo se menja u toku sesije: konobar promeni lokal ili se prijavi drugi korisnik,
    // gost promeni jezik ili temu. Tagovi se osvežavaju bez ijednog poziva ka serveru.
    this.ws.company$.subscribe(() => this.refreshTags());
    this.auth.currentUser.subscribe(() => this.onUserChanged());
    this.theme.theme$.subscribe(() => this.refreshTags());
    this.language.currentLanguage.subscribe(() => this.refreshTags());
  }

  /**
   * Primena serverske odluke. Idempotentno — ponovljeni version frame (reconnect) ne učitava
   * skriptu drugi put.
   */
  apply(policy: ClarityPolicy | null | undefined) {
    try {
      if (!policy || !policy.enabled || !policy.projectID) {
        return;
      }

      this.serverTags = policy.tags || {};

      if (this.loadedProjectID === policy.projectID) {
        this.refreshTags();
        return;
      }

      if (this.loadedProjectID) {
        // Projekat je promenjen u podešavanjima — nova skripta se ne može nakalemiti preko
        // stare; važi od sledećeg učitavanja stranice.
        return;
      }

      this.loadedProjectID = policy.projectID;
      this.load(policy.projectID);
    } catch (e) {
      this.logger.warn('Clarity: primena polise nije uspela', e);
    }
  }

  private load(projectID: string) {
    const w = window as any;

    // Zvanični Clarity snippet: red čekanja se pravi odmah da bi pozivi pre učitavanja
    // skripte bili sačuvani, pa se skripta učitava asinhrono.
    w.clarity = w.clarity || function () {
      (w.clarity.q = w.clarity.q || []).push(arguments);
    };

    const script = document.createElement('script');
    script.async = true;
    script.src = CLARITY_SRC + encodeURIComponent(projectID);
    script.onerror = () => {
      // Nema mreže, blokiran domen (ad blocker), ili stari engine kome skripta ne odgovara.
      this.loadedProjectID = null;
      this.logger.warn('Clarity: skripta nije učitana');
    };

    document.head.appendChild(script);

    this.refreshTags();
  }

  /**
   * Tagovi koje zna sam klijent. Bez njih su snimci na multi-tenant sistemu neupotrebljivi —
   * ne zna se čija je sesija, sa koje verzije ni sa kakvog uređaja.
   */
  private clientTags(): { [key: string]: string } {
    const browser = getBrowserInfo();
    const company = this.ws.company$.value;
    const version = environment.appVersion || '';
    const native = isNativeShell();

    return {
      // Ko i šta
      companyID: company.id ? String(company.id) : '',
      companyName: company.name,
      userID: this.auth.currentUser.value?.userID ? String(this.auth.currentUser.value?.userID) : '',
      app: this.appName(version),
      appVersion: version,

      // Na kom node-u je konekcija — spona ka serverskim logovima uz sessionID.
      serverID: this.ws.serverID,

      // Native shell vs čist browser je prva stvar koja se pita kad nešto ne radi samo "na
      // nekim uređajima" — bridge, štampa i naplata postoje samo u shell-u.
      shell: native ? 'native' : 'browser',
      shellVersion: native ? (SHELL_APP_CONFIG.appVersion || '') : '',

      // Brend browsera odvojeno od Chromium baze: Edge 127 i Samsung 23 umeju da budu na istoj
      // bazi a da se ponašaju različito.
      browser: browser.browserVersion ? `${browser.browser} ${browser.browserVersion}` : (browser.browser || ''),
      engine: browser.engineVersion ? String(browser.engineVersion) : '',

      // Sposobnosti uređaja — sužavaju greške u naplati i fiskalizaciji.
      payment: SHELL_APP_CONFIG.cardPaymentProvider || '',
      printer: SHELL_APP_CONFIG.hasPrinter ? 'true' : 'false',

      // Prelomi teksta su vezani za jezik, a kontrast i čitljivost za temu.
      theme: this.theme.current,
      lang: this.language.currentLanguage.value
    };
  }

  /** Čitljiv naziv aplikacije iz verzije; nepoznat kod ostaje kakav jeste. */
  private appName(version: string): string {
    const code = (version || '').split('-')[1] || '';
    return APP_NAMES[code] || code;
  }

  /**
   * Prijavljen je (drugi) korisnik.
   *
   * Identitet je stabilan po korisniku (`user-<ID>`), pa se sve sesije istog konobara grupišu, a
   * drugi konobar na istom tabletu postaje drugi Clarity korisnik. Kad se korisnik STVARNO
   * promeni, forsira se i nova sesija — inače bi smena delila jednu sesiju. Obično osvežavanje
   * stranice ne pravi novu sesiju, jer korisnik ostaje isti.
   */
  private onUserChanged() {
    const userID = this.auth.currentUser.value?.userID || 0;

    if (userID && userID !== this.lastUserID) {
      const changed = this.lastUserID !== 0;
      this.lastUserID = userID;
      this.call('identify', 'user-' + userID, changed ? this.newVisitID() : undefined);
    } else if (!userID) {
      this.lastUserID = 0;
    }

    this.refreshTags();
  }

  /**
   * Početak nove posete (nov gost) na deljenom uređaju.
   *
   * Clarity pamti "korisnika" 365 dana kroz `_clck` kolačić, pa bi na kiosku svi gosti redom bili
   * JEDAN korisnik sa stotinama sesija — metrike po korisniku bi bile besmislene, a ponašanje
   * uzastopnih gostiju bi sedelo pod istim identifikatorom. Ovim svaka poseta dobija svoj
   * identitet, pa se gosti ne spajaju.
   *
   * Zove ga aplikacija na svojoj prirodnoj granici posete (kiosk: izlazak sa home ekrana u meni).
   * Bez uključenog snimanja je bezopasan no-op.
   */
  startVisit(id?: string) {
    const visitID = id || this.newVisitID();

    // identify(customId, customSessionId) — nov identitet i nova sesija za ovog gosta.
    this.call('identify', visitID, visitID);
    this.setTags({ visitID });
  }


  private newVisitID(): string {
    // Bez crypto.randomUUID: traži Chromium 92+, a prag za snimanje ume da bude niži.
    return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  /** Serverski + klijentski tagovi. Bez učitane skripte je bezopasan no-op. */
  private refreshTags() {
    if (!this.loadedProjectID) {
      return;
    }

    this.setTags(this.serverTags);
    this.setTags(this.clientTags());
  }

  /**
   * Postavlja tagove kroz red čekanja, pa rade i pre nego što se skripta učita.
   * Prazne vrednosti se preskaču — nema smisla filtrirati po praznom tagu.
   */
  private setTags(tags: { [key: string]: string }) {
    Object.keys(tags).forEach(key => {
      const value = tags[key];
      if (key && value) {
        this.call('set', key, value);
      }
    });
  }

  /**
   * Jedini poziv ka Clarity-ju. Pre nego što se biblioteka učita, `window.clarity` je red čekanja
   * koji snippet postavi, pa pozivi ne propadaju — biblioteka ih obradi po učitavanju.
   */
  private call(...args: any[]) {
    const clarity = (window as any).clarity;
    if (typeof clarity !== 'function') {
      return;
    }

    try {
      clarity.apply(null, args);
    } catch (e) {
      this.logger.warn('Clarity: poziv nije uspeo', e);
    }
  }
}
